import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import WaiverRegistrationModal from './WaiverRegistrationModal';
import './Register.css';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showWaiver, setShowWaiver] = useState(false);
  const [tempRegistrationData, setTempRegistrationData] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    // Store registration data and show waiver modal
    const { confirmPassword, ...registrationData } = formData;
    setTempRegistrationData(registrationData);
    setShowWaiver(true);
  };

  const handleWaiverComplete = async (waiverData) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Combine registration data with waiver data
      const completeData = {
        ...tempRegistrationData,
        waiver: {
          signature: waiverData.signature,
          acknowledged: waiverData.acknowledged,
          guardian_name: waiverData.guardian_name,
          adult_players: `${tempRegistrationData.first_name} ${tempRegistrationData.last_name}`
        }
      };

      await api.post('/api/register-with-waiver', completeData);
      
      setSuccess('Registration successful! Waiver signed. Please check your email for verification code.');
      setShowWaiver(false);
      
      // Navigate to verification page after a delay
      setTimeout(() => {
        navigate('/verify-email', { 
          state: { email: tempRegistrationData.email } 
        });
      }, 2000);
      
    } catch (error) {
      setError(error.response?.data?.error || 'Registration failed');
      setShowWaiver(false);
    } finally {
      setLoading(false);
    }
  };

  const handleWaiverCancel = () => {
    setShowWaiver(false);
    setTempRegistrationData(null);
  };

  return (
    <div className="register-container">
      {showWaiver && (
        <WaiverRegistrationModal
          userName={`${formData.first_name} ${formData.last_name}`}
          onComplete={handleWaiverComplete}
          onCancel={handleWaiverCancel}
        />
      )}
      
      <div className="register-card">
        {/* Header Section */}
        <div className="register-header">
          <div className="cricket-logo">
            <div className="cricket-icon">
              🏏
            </div>
          </div>
          <h1 className="academy-title">Join Cricket Academy</h1>
          <p className="academy-subtitle">Create your account to get started</p>
        </div>

        {/* Form Section */}
        <div className="register-form-section">
          {error && (
            <div className="error-alert">
              <span className="alert-icon">⚠️</span>
              {error}
            </div>
          )}

          {success && (
            <div className="success-alert">
              <span className="alert-icon">✅</span>
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="register-form">
            {/* Name Fields Row */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="first_name" className="form-label">
                  <span className="label-icon">👤</span>
                  First Name
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter first name"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="last_name" className="form-label">
                  <span className="label-icon">👤</span>
                  Last Name
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter last name"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Email Field */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                <span className="label-icon">📧</span>
                Email Address
                <span className="required-asterisk">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your email address"
                required
                disabled={loading}
              />
            </div>

            {/* Phone Field */}
            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                <span className="label-icon">📱</span>
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your phone number"
                disabled={loading}
              />
            </div>

            {/* Password Fields Row */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  <span className="label-icon">🔒</span>
                  Password
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Min 6 characters"
                  minLength="6"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">
                  <span className="label-icon">🔒</span>
                  Confirm Password
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Repeat password"
                  minLength="6"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="register-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner"></div>
                  Registering...
                </>
              ) : (
                <>
                  <span className="btn-icon">🎯</span>
                  Register Account
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="register-footer">
            <Link to="/login" className="footer-link">
              Already have an account? <strong>Login here</strong>
            </Link>
          </div>

          {/* Registration Process Info */}
          <div className="registration-info">
            <div className="info-title">
              <span>📋</span>
              Registration Process
            </div>
            <ol className="info-steps">
              <li className="info-step">
                <span className="step-number">1</span>
                Fill out the registration form
              </li>
              <li className="info-step">
                <span className="step-number">2</span>
                Review and sign the adult waiver
              </li>
              <li className="info-step">
                <span className="step-number">3</span>
                Check your email for verification code and waiver confirmation
              </li>
              <li className="info-step">
                <span className="step-number">4</span>
                Wait for admin approval
              </li>
              <li className="info-step">
                <span className="step-number">5</span>
                Start adding kids and track progress
              </li>
            </ol>
            
            <div className="info-warning">
              <span className="warning-icon">⚠️</span>
              <span>
                <strong>Important:</strong> After email verification, your account needs admin approval before you can access the system. 
                You will receive a waiver confirmation email with a copy of your signed waiver.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
